import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { PageContainer, PageTitle } from "../../components/Layout.tsx";
import {
  fetchCategory,
  fetchCategoryDocuments,
  fetchCategoryHierarchy,
} from "../../services/categories.service.ts";
import type { Category, CategoryDocument, CategoryHierarchy, HierarchyNode } from "../../types/Category.ts";
import StyledButton from "../../components/Button.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";
import AddFolderModal from "../../components/AddFolderModal.tsx";
import EditFolderModal from "../../components/EditFolderModal.tsx";
import EditDocumentModal from "../../components/EditDocumentModal.tsx";
import DeleteDocumentModal from "../../components/DeleteDocumentModal.tsx";
import DeleteFolderModal from "../../components/DeleteFolderModal.tsx";
import DeleteCategoryModal from "../../components/DeleteCategoryModal.tsx";
import EditCategoryModal from "../../components/EditCategoryModal.tsx";
import AddDocumentModal from "../../components/AddDocumentModal.tsx";
import CategoryHierarchyTree from "../../components/CategoryHierarchyTree.tsx";
import DocumentFolderView from "../../components/DocumentFolderView.tsx";
import { LucideEdit3Icon, LucideTrashIcon } from "../../icons/lucideBundled.tsx";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const LayoutGrid = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 320px;
  max-width: 360px;
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  margin-bottom: 1.5rem;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const CategoryInfoCard = styled(Card)<{ $compactFooter?: boolean }>`
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$compactFooter &&
    `
    padding-bottom: 0.5rem;
  `}
`;

const CategoryInfoActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.2rem;
  margin-top: -0.55rem;
  margin-bottom: 0;
  padding: 0;
`;

const CategoryInfoIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 31, 82, 0.08);
  }

  .dark-mode & {
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  svg {
    width: 22px;
    height: 22px;
    display: block;
    color: #001f52;
  }

  .dark-mode & svg {
    color: #e0e0e0;
  }
`;

const Label = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #001f52;
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const Value = styled.div`
  font-size: 0.95rem;
  color: #333;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #d0d0d0;
  }
`;

const SearchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.8rem;
  border-radius: 999px;
  border: 2px solid #e0e0e0;
  font-size: 0.9rem;
  background: white;
  color: #001f52;
  transition: border-color 0.3s ease, background 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;
  }

  &:focus {
    outline: none;
    border-color: #64da4c;
    box-shadow: 0 0 0 3px rgba(100, 218, 76, 0.1);
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  border-left: 4px solid #c62828;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background-color: #3a1f1f;
    color: #ff6b6b;
    border-left-color: #ff6b6b;
  }
`;

const LoadingMessage = styled.div`
  color: #666;
  padding: 1rem;
  text-align: center;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const PdfViewerModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const PdfViewerContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  .dark-mode & {
    background: #2a2a3e;
  }
`;

const PdfViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e0e0e0;

  .dark-mode & {
    border-bottom-color: #3a3a4e;
  }
`;

const PdfViewerTitle = styled.h3`
  margin: 0;
  color: #001f52;
  font-size: 1.1rem;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: #001f52;
  }

  .dark-mode & {
    color: #999;

    &:hover {
      background: #3a3a4e;
      color: #e0e0e0;
    }
  }
`;

const PdfViewerFrame = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  border-radius: 0 0 12px 12px;
  background: #f5f5f5;
`;

function findNodeById(
  hierarchy: CategoryHierarchy | null,
  nodeId: number
): HierarchyNode | null {
  if (!hierarchy) {
    return null;
  }
  if (hierarchy.id === nodeId) {
    return hierarchy;
  }

  const search = (node: HierarchyNode): HierarchyNode | null => {
    for (const child of node.children || []) {
      if (child.id === nodeId) {
        return child;
      }
      const found = search(child);
      if (found) {
        return found;
      }
    }
    return null;
  };

  return search(hierarchy);
}

function getPathToNode(hierarchy: CategoryHierarchy, targetNodeId: number): string[] | null {
  if (hierarchy.id === targetNodeId) {
    return [];
  }

  const search = (node: HierarchyNode, parentPath: string[]): string[] | null => {
    for (const child of node.children || []) {
      const path = [...parentPath, `node-${child.id}`];
      if (child.id === targetNodeId) {
        return path;
      }
      const found = search(child, path);
      if (found) {
        return found;
      }
    }
    return null;
  };

  return search(hierarchy, []);
}

function buildCategoryPath(
  categoryType: string,
  categoryId: number,
  nodeId?: number | null
): string {
  const base = `/categories/${categoryType}/${categoryId}`;
  if (nodeId == null) {
    return base;
  }
  return `${base}/hierarchy-nodes/${nodeId}`;
}

function nodeIdFromPath(path: string[]): number | null {
  if (path.length === 0) {
    return null;
  }
  const raw = Number(path[path.length - 1].replace(/^node-/, ""));
  return Number.isNaN(raw) ? null : raw;
}

function getUrlNodeKey(nodeId: number | null): string {
  if (nodeId == null || Number.isNaN(nodeId)) {
    return "root";
  }
  return String(nodeId);
}

export default function CategoryDetails() {
  const { id, type, nodeId: nodeIdParam } = useParams<{
    id: string;
    type: string;
    nodeId?: string;
  }>();
  const navigate = useNavigate();
  const categoryId = Number(id);
  const categoryType = type;
  const urlNodeId =
    nodeIdParam != null && nodeIdParam !== "" ? Number(nodeIdParam) : null;
  const hasInvalidUrlNodeId =
    nodeIdParam != null && nodeIdParam !== "" && Number.isNaN(urlNodeId!);
  const isAtCategoryRoot = nodeIdParam == null || nodeIdParam === "";
  const [category_, setCategory] = useState<Category | null>(null);
  const [documents, setDocuments] = useState<CategoryDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedPdf, setSelectedPdf] = useState<CategoryDocument | null>(null);
  const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isDeleteCategoryOpen, setIsDeleteCategoryOpen] = useState(false);
  
  const [addFormContext, setAddFormContext] = useState<{
    parentNodeId?: number;
  }>({});
  const [editFolderContext, setEditFolderContext] = useState<{
    nodeId: number;
    name: string;
    description: string;
  } | null>(null);
  const [editDocumentContext, setEditDocumentContext] = useState<{
    documentId: number;
    nodeId: number;
    name: string;
    description: string;
  } | null>(null);
  const [deleteDocumentContext, setDeleteDocumentContext] = useState<{
    documentId: number;
    nodeId: number;
    name: string;
  } | null>(null);
  const [deleteFolderContext, setDeleteFolderContext] = useState<{
    nodeId: number;
    name: string;
  } | null>(null);

  const [hierarchy, setHierarchy] = useState<CategoryHierarchy | null>(null);
  const [hierarchySyncKey, setHierarchySyncKey] = useState<string | null>(null);
  const [folderNotFound, setFolderNotFound] = useState(false);
  /** Bumped only on full category fetch — not when name/description is edited in place. */
  const [categoryLoadToken, setCategoryLoadToken] = useState(0);
  const hierarchyRequestRef = useRef(0);
  const folderSyncRequestRef = useRef(0);
  const searchRequestRef = useRef(0);
  const categoryKeyRef = useRef<string | null>(null);

  const urlNodeKey = getUrlNodeKey(urlNodeId);
  const isFolderContentReady = hierarchySyncKey === urlNodeKey;
  const hasSearch = search.trim().length > 0;
  const currentPath = useMemo(() => {
    if (!hierarchy || urlNodeId == null || Number.isNaN(urlNodeId) || folderNotFound) {
      return [];
    }
    return getPathToNode(hierarchy, urlNodeId) ?? [];
  }, [hierarchy, urlNodeId, folderNotFound]);
  const currentNode = useMemo(() => {
    if (!hierarchy || folderNotFound || hasInvalidUrlNodeId) {
      return null;
    }
    if (urlNodeId == null || Number.isNaN(urlNodeId)) {
      return hierarchy;
    }
    return findNodeById(hierarchy, urlNodeId);
  }, [hierarchy, urlNodeId, folderNotFound, hasInvalidUrlNodeId]);
  const isCurrentFolderLeaf =
    Boolean(currentNode) && (currentNode?.children?.length ?? 0) === 0;
  const canUploadToCurrentNode = isCurrentFolderLeaf;

  // Documents are only loaded for leaf folders (no subfolders). Use scope=direct when
  // browsing; scope=subtree when searching so matches include descendant folders.
  const documentsFetchOptions = useMemo(
    () => ({
      q: search.trim() || undefined,
      scope: (hasSearch ? "subtree" : "direct") as "direct" | "subtree",
    }),
    [search, hasSearch]
  );
  const documentsFetchOptionsRef = useRef(documentsFetchOptions);
  documentsFetchOptionsRef.current = documentsFetchOptions;

  const loadDocumentsForUrlNode = useCallback(
    async (
      hier: CategoryHierarchy | null,
      targetUrlNodeId: number | null,
      options: { q?: string; scope?: "direct" | "subtree" },
      isCancelled?: () => boolean
    ) => {
      if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
        return;
      }
      if (hasInvalidUrlNodeId) {
        setDocuments([]);
        setDocumentsLoading(false);
        return;
      }
      if (!hier) {
        setDocuments([]);
        setDocumentsLoading(false);
        return;
      }

      const node =
        targetUrlNodeId == null || Number.isNaN(targetUrlNodeId)
          ? hier
          : findNodeById(hier, targetUrlNodeId);

      const isSubtreeSearch =
        options.scope === "subtree" && Boolean(options.q?.trim());
      const isNonLeaf = (node?.children?.length ?? 0) > 0;

      if (!node || (isNonLeaf && !isSubtreeSearch)) {
        setDocuments([]);
        setDocumentsLoading(false);
        return;
      }

      setDocumentsLoading(true);
      try {
        const docsRes = await fetchCategoryDocuments(
          categoryId,
          categoryType,
          node.id,
          options
        );
        if (isCancelled?.()) {
          return;
        }
        setDocuments(Array.isArray(docsRes) ? docsRes : []);
      } finally {
        if (!isCancelled?.()) {
          setDocumentsLoading(false);
        }
      }
    },
    [categoryId, categoryType, hasInvalidUrlNodeId]
  );

  const syncCurrentFolder = useCallback(
    async (hier: CategoryHierarchy | null, requestId: number) => {
      let notFound = false;
      if (hasInvalidUrlNodeId) {
        notFound = true;
      } else if (urlNodeId != null && !Number.isNaN(urlNodeId)) {
        notFound = !hier || !findNodeById(hier, urlNodeId);
      }

      if (requestId !== folderSyncRequestRef.current) {
        return;
      }

      setFolderNotFound(notFound);
      setHierarchySyncKey(urlNodeKey);

      if (notFound) {
        setDocuments([]);
        return;
      }

      await loadDocumentsForUrlNode(
        hier,
        urlNodeId,
        documentsFetchOptionsRef.current
      );

      if (requestId !== folderSyncRequestRef.current) {
        return;
      }
    },
    [hasInvalidUrlNodeId, loadDocumentsForUrlNode, urlNodeId, urlNodeKey]
  );

  const refreshHierarchy = useCallback(async () => {
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      return;
    }
    const requestId = ++folderSyncRequestRef.current;
    setHierarchySyncKey(null);
    setDocuments([]);

    const hier = await fetchCategoryHierarchy(categoryId, categoryType);
    if (requestId !== folderSyncRequestRef.current) {
      return;
    }
    setHierarchy(hier ?? null);
    await syncCurrentFolder(hier ?? null, requestId);
  }, [categoryId, categoryType, syncCurrentFolder]);

  const refreshDocuments = useCallback(async () => {
    if (hierarchySyncKey !== urlNodeKey) {
      return;
    }
    await loadDocumentsForUrlNode(hierarchy, urlNodeId, documentsFetchOptions);
  }, [
    documentsFetchOptions,
    hierarchy,
    hierarchySyncKey,
    loadDocumentsForUrlNode,
    urlNodeId,
    urlNodeKey,
  ]);

  /** Refetch hierarchy + documents for live search without clearing folder sync state. */
  const refreshSearchResults = useCallback(async () => {
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      return;
    }
    if (hasInvalidUrlNodeId) {
      return;
    }

    const requestId = ++searchRequestRef.current;
    setDocumentsLoading(true);

    try {
      const hier = await fetchCategoryHierarchy(categoryId, categoryType);
      if (requestId !== searchRequestRef.current) {
        return;
      }

      let notFound = false;
      if (urlNodeId != null && !Number.isNaN(urlNodeId)) {
        notFound = !hier || !findNodeById(hier, urlNodeId);
      }

      setHierarchy(hier ?? null);
      setFolderNotFound(notFound);

      if (notFound) {
        setDocuments([]);
        return;
      }

      await loadDocumentsForUrlNode(
        hier ?? null,
        urlNodeId,
        documentsFetchOptionsRef.current,
        () => requestId !== searchRequestRef.current
      );
    } catch (err) {
      if (requestId !== searchRequestRef.current) {
        return;
      }
      console.error("Error refreshing search results:", err);
    } finally {
      if (requestId === searchRequestRef.current) {
        setDocumentsLoading(false);
      }
    }
  }, [categoryId, categoryType, hasInvalidUrlNodeId, loadDocumentsForUrlNode, urlNodeId]);

  const refreshCategory = useCallback(async () => {
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      return;
    }
    try {
      const fetched = await fetchCategory(categoryId, categoryType);
      setCategory((prev) =>
        prev
          ? {
              ...prev,
              pdfDocumentCount: fetched.pdfDocumentCount,
              conversationCount: fetched.conversationCount,
            }
          : fetched
      );
    } catch (err) {
      console.error("Error refreshing category:", err);
    }
  }, [categoryId, categoryType]);

  useEffect(() => {
    if (!id || Number.isNaN(categoryId)) {
      setError("Kategori ID mangler eller er ugyldigt");
      setLoading(false);
      return;
    }
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      setError("Kategori type mangler eller er ugyldig. Gå tilbage til oversigten og åbn kategorien derfra.");
      setLoading(false);
      return;
    }

    const categoryKey = `${categoryType}-${categoryId}`;
    const isNewCategory = categoryKeyRef.current !== categoryKey;
    if (isNewCategory) {
      categoryKeyRef.current = categoryKey;
      setLoading(true);
      setCategory(null);
      setHierarchy(null);
      setHierarchySyncKey(null);
      setDocuments([]);
      setCategoryLoadToken(0);
    }
    setError(null);

    const requestId = ++hierarchyRequestRef.current;

    fetchCategory(categoryId, categoryType)
      .then((pkgRes) => {
        if (requestId !== hierarchyRequestRef.current) {
          return;
        }

        setCategory(pkgRes);
        setCategoryLoadToken((t) => t + 1);
        setError(null);
      })
      .catch((err) => {
        if (requestId !== hierarchyRequestRef.current) {
          return;
        }
        try {
          const userFriendlyError = parseApiError(err);
          setError(userFriendlyError);
        } catch (parseErr) {
          console.error("Error parsing API error:", parseErr);
          setError("Der opstod en fejl. Prøv igen eller kontakt support.");
        }
      })
      .finally(() => {
        if (requestId === hierarchyRequestRef.current) {
          setLoading(false);
        }
      });
  }, [categoryId, categoryType, id]);

  // Refetch hierarchy and load documents when the selected folder changes.
  // Does not refetch category metadata or run when only name/description is edited.
  useEffect(() => {
    if (categoryLoadToken === 0) {
      return;
    }
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      return;
    }

    const requestId = ++folderSyncRequestRef.current;
    setHierarchySyncKey(null);
    setDocuments([]);

    void (async () => {
      try {
        const hier = await fetchCategoryHierarchy(categoryId, categoryType);
        if (requestId !== folderSyncRequestRef.current) {
          return;
        }
        setHierarchy(hier ?? null);
        await syncCurrentFolder(hier ?? null, requestId);
      } catch (err) {
        if (requestId !== folderSyncRequestRef.current) {
          return;
        }
        console.error("Error fetching hierarchy:", err);
      }
    })();
  }, [
    categoryLoadToken,
    categoryId,
    categoryType,
    urlNodeId,
    urlNodeKey,
    syncCurrentFolder,
  ]);

  // When searching: refetch hierarchy + documents (1s debounce) so Placering stays fresh.
  // When search is cleared: reload direct documents for the current folder only.
  useEffect(() => {
    if (!categoryId || Number.isNaN(categoryId)) return;
    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) return;
    if (hierarchySyncKey !== urlNodeKey) return;
    if (folderNotFound || hasInvalidUrlNodeId) return;

    if (hasSearch) {
      setDocumentsLoading(true);
      setDocuments([]);
    }

    const debounceMs = hasSearch ? 1000 : 0;

    const timeoutId = setTimeout(() => {
      if (hasSearch) {
        refreshSearchResults().catch((err) => {
          console.error("Error refreshing search results:", err);
        });
      } else {
        loadDocumentsForUrlNode(hierarchy, urlNodeId, documentsFetchOptions).catch((err) => {
          console.error("Error fetching documents:", err);
        });
      }
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
      searchRequestRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch on search option changes
  }, [documentsFetchOptions, hasSearch]);

  const goToCategoryRoot = useCallback(() => {
    setFolderNotFound(false);

    if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
      return;
    }

    navigate(buildCategoryPath(categoryType, categoryId));
  }, [categoryId, categoryType, navigate]);

  const handlePathChange = useCallback(
    (path: string[]) => {
      setFolderNotFound(false);

      if (!categoryType || (categoryType !== "standard" && categoryType !== "custom")) {
        return;
      }

      const targetNodeId = nodeIdFromPath(path);
      if (path.length > 0 && targetNodeId == null) {
        return;
      }

      const isCurrentFolder =
        targetNodeId == null
          ? isAtCategoryRoot
          : !hasInvalidUrlNodeId && targetNodeId === urlNodeId;

      if (isCurrentFolder) {
        void refreshHierarchy();
        return;
      }

      navigate(buildCategoryPath(categoryType, categoryId, targetNodeId));
    },
    [
      categoryId,
      categoryType,
      hasInvalidUrlNodeId,
      isAtCategoryRoot,
      navigate,
      refreshHierarchy,
      urlNodeId,
    ]
  );

  const handleEditCustomCategory = useCallback(() => {
    if (!category_ || category_.type !== "custom") {
      return;
    }
    setIsEditCategoryOpen(true);
  }, [category_]);

  const handleDeleteCustomCategory = useCallback(() => {
    if (!category_ || category_.type !== "custom") {
      return;
    }
    setIsDeleteCategoryOpen(true);
  }, [category_]);

  const handleOpenDoc = (doc: CategoryDocument) => {
    setSelectedPdf(doc);
  };

  const getPdfUrl = (doc: CategoryDocument): string => {
    if (doc.url) {
      if (doc.url.startsWith("http://") || doc.url.startsWith("https://")) {
        return doc.url;
      }
      return `${window.location.origin}${doc.url}`;
    }
    return "#";
  };

  const closePdfViewer = () => {
    setSelectedPdf(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <PageTitle>Kategorier</PageTitle>
        <LoadingMessage>Indlæser kategori...</LoadingMessage>
      </PageContainer>
    );
  }

  if (error || !category_) {
    return (
      <PageContainer>
        <PageTitle>Kategorier</PageTitle>
        <ErrorMessage>{error || "Kategori ikke fundet"}</ErrorMessage>
        <StyledButton onClick={() => navigate("/categories")}>← Tilbage til kategorier</StyledButton>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <PageTitle noMarginBottom>{category_?.name || "Kategori"}</PageTitle>
        <StyledButton onClick={() => navigate("/categories")}>← Tilbage til kategorier</StyledButton>
      </Header>

      <LayoutGrid>
        <LeftColumn>
          <CategoryInfoCard $compactFooter={category_.type === "custom"}>
            <Label>Beskrivelse</Label>
            <Value>{category_?.description || "Ingen beskrivelse"}</Value>
            <Label>Antal PDF dokumenter</Label>
            <Value>{category_?.pdfDocumentCount ?? 0}</Value>
            <Label>Antal samtaler</Label>
            <Value style={category_.type === "custom" ? { marginBottom: 0 } : undefined}>
              {category_?.conversationCount ?? 0}
            </Value>
            {category_.type === "custom" && (
              <CategoryInfoActionBar>
                <CategoryInfoIconButton
                  type="button"
                  title="Rediger kategori"
                  aria-label="Rediger kategori"
                  onClick={handleEditCustomCategory}
                >
                  <LucideEdit3Icon />
                </CategoryInfoIconButton>
                <CategoryInfoIconButton
                  type="button"
                  title="Slet kategori"
                  aria-label="Slet kategori"
                  onClick={handleDeleteCustomCategory}
                >
                  <LucideTrashIcon />
                </CategoryInfoIconButton>
              </CategoryInfoActionBar>
            )}
          </CategoryInfoCard>

          {hierarchy && (
            <Card>
              <Label>Mappestruktur</Label>
              <CategoryHierarchyTree
                hierarchy={hierarchy}
                currentPath={currentPath}
                onSelectPath={handlePathChange}
              />
            </Card>
          )}

        </LeftColumn>

        <RightColumn>
          <Card>
            {!isFolderContentReady ? (
              <LoadingMessage>Indlæser mappe...</LoadingMessage>
            ) : (
              <>
            {!folderNotFound && (
              <SearchRow>
                <SearchInput
                  placeholder={`Søg i ${
                    currentPath.length === 0
                      ? category_?.name ?? "kategori"
                      : currentNode?.name ?? category_?.name ?? "kategori"
                  }`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchRow>
            )}

            {folderNotFound ? (
              <>
                <ErrorMessage>Mappe ikke fundet</ErrorMessage>
                <StyledButton type="button" onClick={goToCategoryRoot}>
                  Gå til kategoriens rod
                </StyledButton>
              </>
            ) : hierarchy ? (
              <DocumentFolderView
                documents={documents}
                documentsLoading={documentsLoading}
                hierarchy={hierarchy}
                onDocumentClick={handleOpenDoc}
                searchQuery={search}
                currentPath={currentPath}
                onPathChange={handlePathChange}
                categoryName={category_?.name}
                categoryType={category_?.type ?? "custom"}
                onAddFolder={(parentNodeId) => {
                  setAddFormContext({ parentNodeId });
                  setIsAddFolderOpen(true);
                }}
                onEditFolder={(args) => setEditFolderContext(args)}
                onEditDocument={(args) => setEditDocumentContext(args)}
                onDeleteDocument={(args) => setDeleteDocumentContext(args)}
                onDeleteFolder={(args) => setDeleteFolderContext(args)}
              />
            ) : (
              <div style={{
                color: "#666",
                fontSize: "0.9rem",
                padding: "1rem",
                textAlign: "center",
                transition: "color 0.3s ease"
              }} className="dark-mode-text-secondary">
                {loading ? "Indlæser..." : "Ingen mappestruktur tilgængelig."}
              </div>
            )}
              </>
            )}
          </Card>
          
          {category_?.type === "custom" && !hasSearch && canUploadToCurrentNode && isFolderContentReady && currentNode && (
            <Card>
              <Label>Tilføj dokument</Label>
              <Value style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.8rem" }}>
                Dokumenter kan uploades til mapper, der ikke har undermapper.
              </Value>
              <div style={{ marginTop: "1rem" }}>
                <StyledButton type="button" onClick={() => setIsAddDocumentOpen(true)}>
                  + Tilføj dokument
                </StyledButton>
              </div>
            </Card>
          )}

        </RightColumn>
      </LayoutGrid>

      {/* PDF Viewer Modal */}
      <PdfViewerModal $isOpen={selectedPdf !== null} onClick={(e) => {
        if (e.target === e.currentTarget) {
          closePdfViewer();
        }
      }}>
        {selectedPdf && (
          <PdfViewerContent>
            <PdfViewerHeader>
              <PdfViewerTitle>{selectedPdf.name}</PdfViewerTitle>
              <CloseButton onClick={closePdfViewer} title="Luk">×</CloseButton>
            </PdfViewerHeader>
            <PdfViewerFrame
              src={getPdfUrl(selectedPdf)}
              title={selectedPdf.name}
              allow="fullscreen"
            />
          </PdfViewerContent>
        )}
      </PdfViewerModal>

      {category_?.type === "custom" && (
        <>
          <EditCategoryModal
            isOpen={isEditCategoryOpen}
            onClose={() => setIsEditCategoryOpen(false)}
            categoryId={categoryId}
            initialName={category_.name}
            initialDescription={category_.description}
            onSuccess={(updated) => {
              setCategory((prev) =>
                prev && prev.id === updated.id
                  ? {
                      ...prev,
                      name: updated.name,
                      description: updated.description,
                      conversationCount: updated.conversationCount,
                      pdfDocumentCount: updated.pdfDocumentCount,
                    }
                  : prev
              );
            }}
          />
          <AddFolderModal
            isOpen={isAddFolderOpen}
            onClose={() => {
              setIsAddFolderOpen(false);
              setAddFormContext({});
            }}
            categoryId={categoryId}
            parentNodeId={addFormContext.parentNodeId}
            categoryType={categoryType}
            onSuccess={async () => {
              await refreshHierarchy();
            }}
          />
          {currentNode && categoryType && (
            <AddDocumentModal
              isOpen={isAddDocumentOpen}
              onClose={() => setIsAddDocumentOpen(false)}
              categoryId={categoryId}
              categoryType={categoryType}
              nodeId={currentNode.id}
              onSuccess={async () => {
                await Promise.all([refreshDocuments(), refreshCategory()]);
              }}
            />
          )}
          {editFolderContext && (
            <EditFolderModal
              isOpen
              onClose={() => setEditFolderContext(null)}
              categoryId={categoryId}
              nodeId={editFolderContext.nodeId}
              categoryType={categoryType}
              initialName={editFolderContext.name}
              initialDescription={editFolderContext.description}
              onSuccess={async () => {
                await refreshHierarchy();
              }}
            />
          )}
          {editDocumentContext && (
            <EditDocumentModal
              isOpen
              onClose={() => setEditDocumentContext(null)}
              categoryId={categoryId}
              nodeId={editDocumentContext.nodeId}
              documentId={editDocumentContext.documentId}
              initialName={editDocumentContext.name}
              initialDescription={editDocumentContext.description}
              onSuccess={async () => {
                await refreshDocuments();
              }}
            />
          )}
          {deleteDocumentContext && (
            <DeleteDocumentModal
              isOpen
              onClose={() => setDeleteDocumentContext(null)}
              categoryId={categoryId}
              nodeId={deleteDocumentContext.nodeId}
              documentId={deleteDocumentContext.documentId}
              documentName={deleteDocumentContext.name}
              onSuccess={async () => {
                await Promise.all([refreshDocuments(), refreshCategory()]);
              }}
            />
          )}
          {deleteFolderContext && (
            <DeleteFolderModal
              isOpen
              onClose={() => setDeleteFolderContext(null)}
              categoryId={categoryId}
              nodeId={deleteFolderContext.nodeId}
              folderName={deleteFolderContext.name}
              categoryType={categoryType}
              onSuccess={async () => {
                await Promise.all([
                  refreshHierarchy(),
                  refreshDocuments(),
                  refreshCategory(),
                ]);
              }}
            />
          )}
          <DeleteCategoryModal
            isOpen={isDeleteCategoryOpen}
            onClose={() => setIsDeleteCategoryOpen(false)}
            categoryId={categoryId}
            categoryName={category_.name}
            onSuccess={() => {
              navigate("/categories");
            }}
          />
        </>
      )}
    </PageContainer>
  );
}